import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Loader2, Pencil, RefreshCw, RotateCcw, Ticket, XCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { airportIanaZone } from '@/lib/disruptions/airport-tz';
import { providerLabel } from '@/lib/transport/providers';
import {
  useAmendGroupVoucher,
  useCancelGroupVoucher,
  useCancelGroupVoucherCode,
  useGroupVoucher,
  useIssueGroupVoucher,
  useSyncGroupVoucherCodes,
} from '@/lib/transport/use-voucher';
import {
  isVoucherSyncable,
  VOUCHER_STATUS_PRESENTATION,
  type AmendGroupVoucherInput,
  type GroupVoucherAssignmentView,
  type GroupVoucherView,
  type IssueGroupVoucherInput,
  type VoucherHotelRef,
  type VoucherScenario,
} from '@/lib/transport/voucher-types';
import { cn } from '@/lib/cn';
import { FIELD_LABEL_CLASS } from './airport-tokens';
import {
  TransportVoucherIssueDialog,
  type VoucherIssueSubmit,
} from './transport-voucher-issue-dialog';

// The Uber-voucher half of the drawer's transport card (#797) — one voucher PROGRAM
// per PNR group, rendered only once a program exists (`{ voucher: null }` renders
// nothing: issuing is a deliberate operator act, not a default state; the issue
// affordance lands with the fence-sourcing decision).
//
// LIVE, NOT POLLED: one seeding read, then `TransportVoucherChanged` pushes through
// `applyEvent` into `groupVoucherKey(subCaseUrn)` — the claim/redeem badges flip the
// moment Uber's webhook lands, with no timer anywhere in this tree.
//
// PII: the name on a code row is the roster's ALREADY-MASKED `fullName`, matched
// locally by `passengerUrn` — exactly as the trip rows do it.

/** The home point ships street-less: the fence radius does the locating. */
const HOME_FENCE_RADIUS_M = 750;

export interface VoucherHomePoint {
  latitude: number;
  longitude: number;
  address?: string;
}

export function TransportVoucherSection({
  caseUrn,
  subCaseUrn,
  disruptionUrn,
  passengers,
  airportIata,
  hotelRef,
  home,
}: {
  caseUrn: string;
  subCaseUrn: string;
  /** The case's parent disruption — Uber's reconciliation memo groups spend by it. */
  disruptionUrn?: string | undefined;
  /** The roster's passengers for this PNR group — masked names, matched by urn. */
  passengers: readonly { paxUrn: string; fullName: string }[];
  /** The stranded airport (overnight station or disrupted origin) — the pickup side. */
  airportIata?: string | undefined;
  /** The booked hotel's identity (roster denorm) — the HOTEL_OVERNIGHT / ONE_WAY destination. */
  hotelRef: VoucherHotelRef | null;
  /** The declared, geocoded home point of a LOCAL_RESIDENT group — the RIDE_HOME destination. */
  home: VoucherHomePoint | null;
}) {
  const { t } = useTranslation('pnrActions');
  const voucherQuery = useGroupVoucher(caseUrn, subCaseUrn);
  const issue = useIssueGroupVoucher();
  const amend = useAmendGroupVoucher();
  const cancel = useCancelGroupVoucher();
  const cancelCode = useCancelGroupVoucherCode();
  const sync = useSyncGroupVoucherCodes();

  const [issueOpen, setIssueOpen] = useState(false);
  const [issueError, setIssueError] = useState<string | null>(null);
  const [amendOpen, setAmendOpen] = useState(false);
  const [amendError, setAmendError] = useState<string | null>(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [codeCancel, setCodeCancel] = useState<{ codeId: string; passengerLabel: string } | null>(null);
  const [codeCancelError, setCodeCancelError] = useState<string | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);

  const nameOf = useMemo(() => {
    const byUrn = new Map(passengers.map((p) => [p.paxUrn, p.fullName]));
    return (paxUrn: string): string => byUrn.get(paxUrn) ?? shortUrn(paxUrn);
  }, [passengers]);

  if (voucherQuery.isError) {
    return (
      <div
        className="flex items-center justify-between gap-2 rounded-md border border-border p-2 text-xs"
        data-testid="transport-voucher-error"
        role="alert"
      >
        <span className="flex items-start gap-1.5 text-destructive">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          {t('transport.voucher.loadError')}
        </span>
        <Button type="button" variant="outline" size="sm" onClick={() => void voucherQuery.refetch()}>
          <RotateCcw className="h-3 w-3" aria-hidden />
          {t('transport.retry')}
        </Button>
      </div>
    );
  }

  // What this group can actually be issued: HOTEL scenarios need the booked hotel's
  // identity, RIDE_HOME needs the geocoded home point — and every scenario needs the
  // airport. Nothing issuable + no program ⇒ the section stays silent.
  const scenarios: VoucherScenario[] = [
    ...(hotelRef ? (['HOTEL_OVERNIGHT', 'ONE_WAY'] as const) : []),
    ...(home ? (['RIDE_HOME'] as const) : []),
  ];
  const issuable = !!airportIata && scenarios.length > 0;

  const confirmIssue = async (submit: VoucherIssueSubmit): Promise<void> => {
    setIssueError(null);
    const startsAt = Date.now();
    const input: IssueGroupVoucherInput = {
      scenario: submit.scenario,
      ...(disruptionUrn ? { disruptionUrn } : {}),
      airportIata: airportIata as string,
      ...(submit.scenario === 'RIDE_HOME' && home
        ? {
            destination: {
              latitude: home.latitude,
              longitude: home.longitude,
              radiusMeters: HOME_FENCE_RADIUS_M,
              address: home.address ?? t('transport.voucher.homeFallback'),
            },
          }
        : { hotelRef: hotelRef as VoucherHotelRef }),
      startsAt,
      endsAt: startsAt + submit.validityHours * 3_600_000,
      timezone: airportIanaZone(airportIata),
      currencyCode: 'USD',
      passengerUrns: passengers.map((p) => p.paxUrn),
      reason: submit.reason,
    };
    try {
      await issue.mutateAsync({ caseUrn, subCaseUrn, input });
      setIssueOpen(false);
    } catch (e) {
      setIssueError(errorMessage(e, t('transport.voucher.issueFailed')));
    }
  };

  const voucher = voucherQuery.data;
  if (!voucher) {
    if (!issuable) return null;
    return (
      <div className="flex items-center justify-between gap-2 rounded-md border border-dashed border-border p-2">
        <p className={cn('flex items-center gap-1.5', FIELD_LABEL_CLASS)}>
          <Ticket className="h-3.5 w-3.5" aria-hidden />
          {/* Vendor-neutral on purpose (T4): which vendor will issue is the server's pick. */}
          {t('transport.voucher.titleGeneric')}
        </p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-7 gap-1 px-2 text-xs"
          onClick={() => {
            setIssueError(null);
            setIssueOpen(true);
          }}
          data-testid="transport-voucher-issue-open"
        >
          <Ticket className="h-3 w-3" aria-hidden />
          {t('transport.voucher.issueOpen')}
        </Button>
        {issueOpen ? (
          <TransportVoucherIssueDialog
            scenarios={scenarios}
            paxCount={passengers.length}
            homeLabel={home?.address}
            pending={issue.isPending}
            error={issueError}
            onOpenChange={(open) => {
              if (!open) setIssueOpen(false);
            }}
            onConfirm={(submit) => void confirmIssue(submit)}
          />
        ) : null}
      </div>
    );
  }

  const presentation = VOUCHER_STATUS_PRESENTATION[voucher.status];
  const cancellable = voucher.status !== 'CANCELLED';
  // Amend needs a program alive at the vendor (M4): ISSUING has none yet, CANCELLED refuses.
  const amendable = voucher.status === 'ACTIVE' || voucher.status === 'CODES_PENDING';

  const confirmAmend = async (input: AmendGroupVoucherInput): Promise<void> => {
    setAmendError(null);
    try {
      await amend.mutateAsync({ caseUrn, subCaseUrn, input });
      setAmendOpen(false);
    } catch (e) {
      setAmendError(errorMessage(e, t('transport.voucher.amendFailed')));
    }
  };

  const confirmCancel = async (reason: string): Promise<void> => {
    setCancelError(null);
    try {
      await cancel.mutateAsync({ caseUrn, subCaseUrn, reason });
      setCancelOpen(false);
    } catch (e) {
      setCancelError(errorMessage(e, t('transport.voucher.cancelFailed')));
    }
  };

  const confirmCancelCode = async (reason: string): Promise<void> => {
    if (!codeCancel) return;
    setCodeCancelError(null);
    try {
      await cancelCode.mutateAsync({ caseUrn, subCaseUrn, codeId: codeCancel.codeId, reason });
      setCodeCancel(null);
    } catch (e) {
      setCodeCancelError(errorMessage(e, t('transport.voucher.codeCancelFailed')));
    }
  };

  const runSync = async (): Promise<void> => {
    setSyncError(null);
    try {
      await sync.mutateAsync({ caseUrn, subCaseUrn });
    } catch (e) {
      setSyncError(errorMessage(e, t('transport.voucher.syncFailed')));
    }
  };

  return (
    <section
      className="space-y-2 rounded-md border border-border p-2.5"
      data-testid="transport-voucher"
      data-voucher-status={voucher.status}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className={cn('flex items-center gap-1.5', FIELD_LABEL_CLASS)}>
          <Ticket className="h-3.5 w-3.5" aria-hidden />
          {/* The action/thing, not the brand (PO 2026-10-29) — the vendor reads below as metadata. */}
          {t('transport.voucher.titleGeneric')}
          {voucher.revision > 1 ? (
            <span className="text-muted-foreground/80" title={t('transport.voucher.reissuedHint')}>
              r{voucher.revision}
            </span>
          ) : null}
        </p>
        <div className="flex items-center gap-1.5">
          <Badge variant={presentation.tone} size="sm" data-testid="transport-voucher-status">
            {t(presentation.labelKey)}
          </Badge>
          {isVoucherSyncable(voucher.status) ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 gap-1 px-2 text-xs"
              disabled={sync.isPending}
              onClick={() => void runSync()}
              data-testid="transport-voucher-sync"
            >
              {sync.isPending ? (
                <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
              ) : (
                <RefreshCw className="h-3 w-3" aria-hidden />
              )}
              {t('transport.voucher.syncCodes')}
            </Button>
          ) : null}
          {amendable ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 gap-1 px-2 text-xs"
              onClick={() => {
                setAmendError(null);
                setAmendOpen(true);
              }}
              data-testid="transport-voucher-amend-open"
            >
              <Pencil className="h-3 w-3" aria-hidden />
              {t('transport.voucher.amendOpen')}
            </Button>
          ) : null}
          {cancellable ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 gap-1 px-2 text-xs"
              onClick={() => {
                setCancelError(null);
                setCancelOpen(true);
              }}
              data-testid="transport-voucher-cancel-open"
            >
              <XCircle className="h-3 w-3" aria-hidden />
              {t('common:common.cancel')}
            </Button>
          ) : null}
        </div>
      </div>

      <p className="text-xs text-muted-foreground">
        {t('transport.voucher.validity', {
          from: formatWindow(voucher.startsAt, voucher.timezone),
          to: formatWindow(voucher.endsAt, voucher.timezone),
        })}
        {/* The brand as a detail: after issuance "which app does the pax open?" is real support info. */}
        {' · '}
        {providerLabel(voucher.provider)}
        {' · '}
        {t('transport.voucher.trips', { n: voucher.maxTripsPerVoucher })}
        {typeof voucher.capPerTripUsd === 'number' ? (
          <> · {t('transport.voucher.capPerTrip', { cap: voucher.capPerTripUsd })}</>
        ) : null}
      </p>

      {voucher.status === 'CANCELLED' && voucher.cancelledReason ? (
        <p className="text-xs text-muted-foreground" data-testid="transport-voucher-cancelled-reason">
          {t('transport.voucher.cancelledReason', { reason: voucher.cancelledReason })}
        </p>
      ) : null}

      {voucher.lastError ? (
        <p className="flex items-start gap-1.5 text-xs text-destructive" data-testid="transport-voucher-last-error">
          <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" aria-hidden />
          <span>{voucher.lastError}</span>
        </p>
      ) : null}

      {voucher.assignments.length > 0 ? (
        <ul className="divide-y divide-border">
          {voucher.assignments.map((assignment) => (
            <AssignmentRow
              key={assignment.codeId}
              assignment={assignment}
              passengerLabel={nameOf(assignment.passengerUrn)}
              cancelled={voucher.status === 'CANCELLED'}
              {...(cancellable
                ? {
                    onCancelCode: () =>
                      setCodeCancel({ codeId: assignment.codeId, passengerLabel: nameOf(assignment.passengerUrn) }),
                  }
                : {})}
            />
          ))}
        </ul>
      ) : (
        <p className="text-xs text-muted-foreground" data-testid="transport-voucher-no-codes">
          {t('transport.voucher.noCodes')}
        </p>
      )}

      {syncError ? (
        <p className="text-xs text-destructive" role="alert">
          {syncError}
        </p>
      ) : null}

      {codeCancel ? (
        <VoucherCodeCancelDialog
          passengerLabel={codeCancel.passengerLabel}
          pending={cancelCode.isPending}
          error={codeCancelError}
          onOpenChange={(open) => {
            if (!open) setCodeCancel(null);
          }}
          onConfirm={(reason) => void confirmCancelCode(reason)}
        />
      ) : null}

      {amendOpen ? (
        <VoucherAmendDialog
          voucher={voucher}
          hotelRef={hotelRef}
          pending={amend.isPending}
          error={amendError}
          onOpenChange={(open) => {
            if (!open) setAmendOpen(false);
          }}
          onConfirm={(input) => void confirmAmend(input)}
        />
      ) : null}

      {cancelOpen ? (
        <VoucherCancelDialog
          voucher={voucher}
          pending={cancel.isPending}
          error={cancelError}
          onOpenChange={(open) => {
            if (!open) setCancelOpen(false);
          }}
          onConfirm={(reason) => void confirmCancel(reason)}
        />
      ) : null}
    </section>
  );
}

/**
 * One passenger's code. The claim marks are VENDOR truth (webhook-written): the
 * strongest state wins the badge — redeemed > claimed > not claimed. The code text
 * is what an operator reads out over a counter, so it renders monospaced and whole.
 */
function AssignmentRow({
  assignment,
  passengerLabel,
  cancelled,
  onCancelCode,
}: {
  assignment: GroupVoucherAssignmentView;
  passengerLabel: string;
  cancelled: boolean;
  /** Present only while the program is alive — a cancelled code offers nothing. */
  onCancelCode?: () => void;
}) {
  const { t } = useTranslation('pnrActions');
  const codeCancelled = Boolean(assignment.cancelledAt);
  const claim = assignment.redeemedAt
    ? { labelKey: 'transport.voucher.redeemed', tone: 'success' as const, at: assignment.redeemedAt }
    : assignment.claimedAt
      ? { labelKey: 'transport.voucher.claimed', tone: 'info' as const, at: assignment.claimedAt }
      : { labelKey: 'transport.voucher.unclaimed', tone: 'muted' as const, at: undefined };

  return (
    <li
      className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1 py-1.5 text-xs first:pt-0 last:pb-0"
      data-testid="transport-voucher-pax"
    >
      <span className="min-w-0 flex-1 truncate" title={assignment.passengerUrn}>
        {passengerLabel}
      </span>
      <span className="font-mono text-[11px]" data-testid="transport-voucher-code">
        {assignment.codeText}
      </span>
      <span className="flex items-center gap-1.5">
        {codeCancelled ? (
          <Badge
            variant="error"
            size="sm"
            title={assignment.cancelledReason ?? assignment.cancelledAt}
            data-testid="transport-voucher-code-cancelled"
          >
            {t('transport.voucher.codeCancelled')}
          </Badge>
        ) : (
          <Badge variant={claim.tone} size="sm" title={claim.at}>
            {t(claim.labelKey)}
          </Badge>
        )}
        {!cancelled && !codeCancelled ? (
          <a
            href={assignment.redemptionLink}
            target="_blank"
            rel="noreferrer"
            className="font-medium text-primary hover:underline"
          >
            {t('transport.voucher.openLink')}
          </a>
        ) : null}
        {!codeCancelled && onCancelCode ? (
          <button
            type="button"
            onClick={onCancelCode}
            className="font-medium text-destructive hover:underline"
            data-testid="transport-voucher-code-cancel-open"
          >
            {t('transport.voucher.codeCancelOpen')}
          </button>
        ) : null}
      </span>
    </li>
  );
}

/** Cancel ONE code (#826) - same discipline as the program cancel: mandatory audited reason. */
function VoucherCodeCancelDialog({
  passengerLabel,
  pending,
  error,
  onOpenChange,
  onConfirm,
}: {
  passengerLabel: string;
  pending: boolean;
  error?: string | null;
  onOpenChange: (open: boolean) => void;
  onConfirm: (reason: string) => void;
}) {
  const { t } = useTranslation('pnrActions');
  const [reason, setReason] = useState('');
  const [validation, setValidation] = useState<string | null>(null);

  const submit = (): void => {
    const trimmed = reason.trim();
    if (!trimmed) {
      setValidation(t('transport.voucher.noReason'));
      return;
    }
    setValidation(null);
    onConfirm(trimmed);
  };

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent data-testid="transport-voucher-code-cancel-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <XCircle className="h-4 w-4" aria-hidden /> {t('transport.voucher.codeCancelTitle')}
          </DialogTitle>
          <DialogDescription>
            {t('transport.voucher.codeCancelDescription', { passenger: passengerLabel })}
          </DialogDescription>
        </DialogHeader>

        <label className="block text-sm">
          <span className="mb-1 block font-medium">{t('reason.requiredLabel')}</span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder={t('transport.voucher.reasonPlaceholder')}
            className="w-full rounded-md border border-input bg-background p-2 text-sm"
            data-testid="transport-voucher-code-cancel-reason"
          />
        </label>
        {validation ? (
          <p className="text-xs text-destructive" role="alert">
            {validation}
          </p>
        ) : null}
        {error ? (
          <p className="text-xs text-destructive" role="alert">
            {error}
          </p>
        ) : null}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>
            {t('transport.voucher.keep')}
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={submit}
            disabled={pending}
            data-testid="transport-voucher-code-cancel-confirm"
          >
            {pending ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : null}
            {t('transport.voucher.codeCancelConfirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * In-place amendment (M4): PATCH the live program at the vendor — codes, claims and
 * the revision survive, so nobody re-claims anything. The console offers what it can
 * source: the BOOKED hotel as the new destination (reference, confirmed by Booking)
 * and/or a window extension. Same mandatory audited reason as every other write.
 */
function VoucherAmendDialog({
  voucher,
  hotelRef,
  pending,
  error,
  onOpenChange,
  onConfirm,
}: {
  voucher: GroupVoucherView;
  hotelRef: VoucherHotelRef | null;
  pending: boolean;
  error?: string | null;
  onOpenChange: (open: boolean) => void;
  onConfirm: (input: AmendGroupVoucherInput) => void;
}) {
  const { t } = useTranslation('pnrActions');
  const [retarget, setRetarget] = useState(false);
  const [extendHours, setExtendHours] = useState('');
  const [reason, setReason] = useState('');
  const [validation, setValidation] = useState<string | null>(null);

  const submit = (): void => {
    const trimmed = reason.trim();
    const hours = extendHours ? Number(extendHours) : 0;
    if (!(retarget && hotelRef) && !hours) {
      setValidation(t('transport.voucher.amendNothing'));
      return;
    }
    if (!trimmed) {
      setValidation(t('transport.voucher.noReason'));
      return;
    }
    setValidation(null);
    onConfirm({
      ...(retarget && hotelRef ? { hotelRef } : {}),
      ...(hours ? { endsAt: voucher.endsAt + hours * 3_600_000 } : {}),
      reason: trimmed,
    });
  };

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent data-testid="transport-voucher-amend-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="h-4 w-4" aria-hidden /> {t('transport.voucher.amendTitle')}
          </DialogTitle>
          <DialogDescription>
            {t('transport.voucher.amendDescription', { provider: providerLabel(voucher.provider) })}
          </DialogDescription>
        </DialogHeader>

        {hotelRef ? (
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              checked={retarget}
              onChange={(e) => setRetarget(e.target.checked)}
              className="mt-0.5"
              data-testid="transport-voucher-amend-hotel"
            />
            <span>{t('transport.voucher.amendHotel')}</span>
          </label>
        ) : null}

        <label className="block text-sm">
          <span className="mb-1 block font-medium">{t('transport.voucher.amendExtendLabel')}</span>
          <select
            value={extendHours}
            onChange={(e) => setExtendHours(e.target.value)}
            className="w-full rounded-md border border-input bg-background p-2 text-sm"
            data-testid="transport-voucher-amend-extend"
          >
            <option value="">{t('transport.voucher.amendExtendNone')}</option>
            {[6, 12, 24, 48].map((n) => (
              <option key={n} value={String(n)}>
                {t('transport.voucher.amendExtendHours', { n })}
              </option>
            ))}
          </select>
        </label>

        <label className="block text-sm">
          <span className="mb-1 block font-medium">{t('reason.requiredLabel')}</span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder={t('transport.voucher.reasonPlaceholderAmend')}
            className="w-full rounded-md border border-input bg-background p-2 text-sm"
            data-testid="transport-voucher-amend-reason"
          />
        </label>

        {validation ? (
          <p className="text-xs text-destructive" role="alert">
            {validation}
          </p>
        ) : null}
        {error ? (
          <p className="text-xs text-destructive" role="alert">
            {error}
          </p>
        ) : null}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>
            {t('transport.voucher.keep')}
          </Button>
          <Button
            type="button"
            onClick={submit}
            disabled={pending}
            data-testid="transport-voucher-amend-confirm"
          >
            {pending ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : null}
            {t('transport.voucher.amendConfirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Cancel the whole program — mirrors `TransportCancelDialog` (never a `window.confirm`). */
function VoucherCancelDialog({
  voucher,
  pending,
  error,
  onOpenChange,
  onConfirm,
}: {
  voucher: GroupVoucherView;
  pending: boolean;
  error?: string | null;
  onOpenChange: (open: boolean) => void;
  onConfirm: (reason: string) => void;
}) {
  const { t } = useTranslation('pnrActions');
  const [reason, setReason] = useState('');
  const [validation, setValidation] = useState<string | null>(null);

  const submit = (): void => {
    const trimmed = reason.trim();
    if (!trimmed) {
      setValidation(t('transport.voucher.noReason'));
      return;
    }
    setValidation(null);
    onConfirm(trimmed);
  };

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent data-testid="transport-voucher-cancel-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <XCircle className="h-4 w-4" aria-hidden /> {t('transport.voucher.cancelTitle')}
          </DialogTitle>
          <DialogDescription>
            {t('transport.voucher.cancelDescription', {
              n: voucher.assignments.length,
              provider: providerLabel(voucher.provider),
            })}
          </DialogDescription>
        </DialogHeader>

        <label className="block text-sm">
          <span className="mb-1 block font-medium">{t('reason.requiredLabel')}</span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder={t('transport.voucher.reasonPlaceholder')}
            className="w-full rounded-md border border-input bg-background p-2 text-sm"
            data-testid="transport-voucher-cancel-reason"
          />
        </label>

        {validation ? (
          <p className="text-xs text-destructive" role="alert">
            {validation}
          </p>
        ) : null}
        {error ? (
          <p className="text-xs text-destructive" role="alert">
            {error}
          </p>
        ) : null}

        <DialogFooter>
          <Button type="button" variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            {t('transport.voucher.keep')}
          </Button>
          <Button
            type="button"
            variant="destructive"
            size="sm"
            disabled={pending}
            onClick={submit}
            data-testid="transport-voucher-cancel-confirm"
          >
            {pending ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : null}
            {t('transport.voucher.cancelConfirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Window stamp in the PROGRAM's timezone (the care window is defined there). */
function formatWindow(epochMs: number, timezone: string): string {
  try {
    return new Intl.DateTimeFormat(undefined, {
      timeZone: timezone,
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(epochMs));
  } catch {
    return new Date(epochMs).toISOString();
  }
}

function shortUrn(urn: string): string {
  const parts = urn.split(':');
  return parts[parts.length - 1] || urn;
}

function errorMessage(error: unknown, fallback: string): string {
  const message = error instanceof Error ? error.message : '';
  return message && message.length < 200 ? message : fallback;
}
